import { useTheme } from "@mui/material";
import { Automatic20Px } from "@qubit/autoparts";
import { parse, stringify } from "qs";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { useConfigurePortsWithRetry } from "~/hooks/useConfigurePortsWithRetry";
import { useFcNavigate } from "~/hooks/useFcNavigate";

import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { sendUserTaskSelectedEvent } from "~/redux/actions/userEvents";
import { selectUserCanCycleCount } from "~/redux/selectors/authSelectors";
import {
  selectThisWorkstation,
  selectWorkstationHasInventoryRole
} from "~/redux/selectors/workstationsSelectors";

import { MainButton } from "./MainButton";
import { PreparingWorkstationModal } from "./PreparingWorkstationModal";

export type Props = {
  route: string;
};

export function CycleCountsButton({ route }: Props) {
  const { t } = useTranslation();
  const navigate = useFcNavigate();
  const { palette } = useTheme();
  const dispatch = useAppDispatch();
  const { search }: { search: string } = useLocation();
  const trackedPageName = useMixpanelPageName();

  const userCanCycleCount = useAppSelector(selectUserCanCycleCount);
  const isInventoryRoleFound = useAppSelector(
    selectWorkstationHasInventoryRole
  );
  const hasWorkstation = !!useAppSelector(selectThisWorkstation);

  const { configurePortsWithRetry, isConfigurePortsRetrying } =
    useConfigurePortsWithRetry({
      workstationMode: "Cycle Count"
    });

  const onCycleCountButtonClicked = async () => {
    if (userCanCycleCount && isInventoryRoleFound) {
      const isConfigurePortsSuccess = await configurePortsWithRetry();
      if (!isConfigurePortsSuccess) {
        return;
      }

      await dispatch(sendUserTaskSelectedEvent("CYCLECOUNT"));

      navigate({
        pathname: route,
        search: stringify({
          ...parse(search, { ignoreQueryPrefix: true }),
          autostore: true
        })
      });
      mixpanelTrack({
        type: "Button Click",
        label:
          route === "/autostore-cyclecount"
            ? "Cycle Counts V1 Button"
            : "Cycle Counts V2 Button",
        trackedPageName
      });
    }
  };

  const alert =
    (!hasWorkstation && t("no workstation selected")) ||
    (!isInventoryRoleFound && hasWorkstation && t("no inventory role found")) ||
    (!userCanCycleCount && t("user has insufficient privileges")) ||
    undefined;

  const alertKind =
    (!hasWorkstation && "workstation") ||
    (!isInventoryRoleFound && hasWorkstation && "workstation") ||
    (!userCanCycleCount && "user") ||
    undefined;

  return (
    <>
      <MainButton
        alert={alert}
        alertKind={alertKind}
        disabled={!userCanCycleCount || !isInventoryRoleFound}
        onClick={onCycleCountButtonClicked}
        variant="md"
        icon={
          <Automatic20Px
            width={30}
            height={30}
            fill={palette.primary.contrastText}
          />
        }
      >
        {t("nav.viewname.cycle counts")}
      </MainButton>

      <PreparingWorkstationModal isOpen={isConfigurePortsRetrying} />
    </>
  );
}
